import type { GridShapesMenu, Shape } from "~/types/grid.types";
import { CIRCLES_SQUARE_SHAPES } from "./shapesCirclesSquare";
import { ENGRAVED_1X1_SQUARE_SHAPES } from "./shapesEngraved1x1Square";
import { ENGRAVED_2X2_SQUARE_SHAPES } from "./shapesEngraved2x2Square";
import { PEAK_SQUARE_SHAPES } from "./shapesPeakSquare";
import { PRINTED_SQUARE_SHAPES } from "./shapesPrintedSquare";
import { SHAPE_IDS } from "./shapesIdsSquare";
import { WOOD_SLAT_SQUARE_SHAPES } from "./woodSlats";

export { SHAPE_IDS };

const BASIC_SHAPES: Shape[] = [
  {
    type: "rect",
    id: SHAPE_IDS.LARGE_SQUARE,
    icon: "LargeSquare",
    prefix: "LargeSquare",
    label: "Large Square",
    w: 240,
    h: 240,
  },
  {
    type: "rect",
    id: SHAPE_IDS.SQUARE,
    icon: "Square",
    prefix: "Blank",
    label: "Square",
    w: 120,
    h: 120,
  },
  {
    type: "rect",
    id: SHAPE_IDS.QUARTER,
    icon: "Quarter",
    prefix: "Quarter",
    label: "Quarter",
    w: 60,
    h: 60,
  },
  {
    type: "rect",
    id: SHAPE_IDS.PIXEL,
    icon: "Pixel",
    prefix: "Pixel",
    label: "Pixel",
    w: 30,
    h: 30,
  },
  {
    type: "rect",
    id: SHAPE_IDS.BIT,
    icon: "Bit",
    prefix: "Bit",
    label: "Bit",
    w: 20,
    h: 20,
    admin: true,
  },
  {
    type: "rect",
    id: SHAPE_IDS.LARGE_HALF_LAP_VERTICAL,
    icon: "LargeHalflapVertical",
    prefix: "LargeHalflap",
    label: "Large Halflap",
    w: 120,
    h: 240,

    children: [
      {
        type: "rect",
        id: SHAPE_IDS.LARGE_HALF_LAP_HORIZONTAL,
        icon: "LargeHalflapHorizontal",
        prefix: "LargeHalflap",
        label: "Large Halflap Horizontal",
        w: 240,
        h: 120,
      },
    ],
  },
  {
    type: "rect",
    id: SHAPE_IDS.HALF_LAP_VERTICAL,
    icon: "HalflapVertical",
    prefix: "Half",
    label: "Halflap",
    w: 60,
    h: 120,

    children: [
      {
        type: "rect",
        id: SHAPE_IDS.HALF_LAP_HORIZONTAL,
        icon: "HalflapHorizontal",
        prefix: "Half",
        label: "Halflap Horizontal",
        w: 120,
        h: 60,
      },
    ],
  },
  {
    type: "rect",
    id: SHAPE_IDS.SHIPLAP_VERTICAL,
    icon: "ShiplapHorizontal",
    prefix: "Shiplap",
    label: "Shiplap",
    w: 240,
    h: 60,
    rotation: 90,
    children: [
      {
        type: "rect",
        id: SHAPE_IDS.SHIPLAP_HORIZONTAL,
        icon: "ShiplapHorizontal",
        prefix: "Shiplap",
        label: "Shiplap Horizontal",
        w: 240,
        h: 60,
        rotation: 0,
      },
    ],
  },
  {
    type: "rect",
    id: SHAPE_IDS.MINI_SHIPLAP_VERTICAL,
    icon: "MiniShiplapHorizontal",
    prefix: "MiniShiplap",
    label: "Mini Shiplap",
    w: 120,
    h: 30,
    rotation: 90,
    children: [
      {
        type: "rect",
        id: SHAPE_IDS.MINI_SHIPLAP_HORIZONTAL,
        icon: "MiniShiplapHorizontal",
        prefix: "MiniShiplap",
        label: "Mini Shiplap Horizontal",
        w: 120,
        h: 30,
        rotation: 0,
      },
    ],
  },
  {
    type: "rect-triangle",
    id: SHAPE_IDS.LEFT_BOTTOM_TRIANGLE,
    icon: "RightTopTriangle",
    prefix: "Triangle",
    label: "Triangle",
    rotation: 180,
    w: 120,
    h: 120,
    children: [
      {
        type: "rect-triangle",
        id: SHAPE_IDS.RIGHT_TOP_TRIANGLE,
        icon: "RightTopTriangle",
        prefix: "Triangle",
        label: "Right Top Triangle",
        rotation: 0,
        w: 120,
        h: 120,
      },
      {
        type: "rect-triangle",
        id: SHAPE_IDS.LEFT_TOP_TRIANGLE,
        icon: "RightTopTriangle",
        prefix: "Triangle",
        label: "Left Top Triangle",
        rotation: 270,
        w: 120,
        h: 120,
      },
      {
        type: "rect-triangle",
        id: SHAPE_IDS.RIGHT_BOTTOM_TRIANGLE,
        icon: "RightTopTriangle",
        prefix: "Triangle",
        label: "Right Bottom Triangle",
        rotation: 90,
        w: 120,
        h: 120,
      },
    ],
  },
  {
    type: "rect-triangle",
    id: SHAPE_IDS.SMALL_90_LEFT_BOTTOM_TRIANGLE,
    icon: "RightTopTriangle",
    prefix: "Small90Triangle",
    imgRotation: 270,
    label: "Small 90 Triangle",
    rotation: 180,

    w: 60,
    h: 60,
    children: [
      {
        type: "rect-triangle",
        id: SHAPE_IDS.SMALL_90_RIGHT_TOP_TRIANGLE,
        icon: "RightTopTriangle",
        prefix: "Small90Triangle",
        imgRotation: 270,

        label: "Small 90 Right Top Triangle",
        rotation: 0,
        w: 60,
        h: 60,
      },
      {
        type: "rect-triangle",
        id: SHAPE_IDS.SMALL_90_LEFT_TOP_TRIANGLE,
        icon: "RightTopTriangle",
        prefix: "Small90Triangle",
        imgRotation: 270,

        label: "Small 90 Left Top Triangle",
        rotation: 270,
        w: 60,
        h: 60,
      },
      {
        type: "rect-triangle",
        id: SHAPE_IDS.SMALL_90_RIGHT_BOTTOM_TRIANGLE,
        icon: "RightTopTriangle",
        prefix: "Small90Triangle",
        imgRotation: 270,

        label: "Small 90 Right Bottom Triangle",
        rotation: 90,
        w: 60,
        h: 60,
      },
    ],
  },
];

export const SHAPES: Shape[] = [
  ...BASIC_SHAPES,
  ...CIRCLES_SQUARE_SHAPES,
  ...ENGRAVED_1X1_SQUARE_SHAPES,
  ...ENGRAVED_2X2_SQUARE_SHAPES,
  ...PRINTED_SQUARE_SHAPES,
  ...PEAK_SQUARE_SHAPES,
  ...WOOD_SLAT_SQUARE_SHAPES,
];

export const MENU: GridShapesMenu = {
  list: [
    {
      title: "Shapes:",
      collapse: true,
      items: [
        ...SHAPES.filter((s: Shape) =>
          ["LargeSquare", "Square", "Quarter", "Pixel", "Bit"].includes(s.id),
        ),
        { breakpoint: true },
        ...SHAPES.filter((s: Shape) =>
          [
            "LargeHalflapVertical",
            "HalflapVertical",
            "ShiplapVertical",
            "MiniShiplapVertical",
            "LeftBottomTriangle",
            "Small90LeftBottomTriangle",
          ].includes(s.id),
        ),
        { breakpoint: true },
        ...SHAPES.filter((s: Shape) =>
          [
            "LargeHalfCircleVertical",
            "QuarterCircleBottomLeft",
            "HalfCircleVertical",
            "SmallHalfCircleVertical",
          ].includes(s.id),
        ),
      ],
    },
    {
      title: "Wood Slats:",
      collapse: true,
      items: WOOD_SLAT_SQUARE_SHAPES,
    },
    {
      title: "Engraved 1' x 1' Tiles:",
      collapse: true,
      items: [
        ...SHAPES.filter((s: Shape) =>
          [
            "EngravedBackSlash",
            "EngravedCross",
            "EngravedFourSquare",
            "EngravedParallel",
          ].includes(s.id),
        ),
      ],
    },
    {
      title: "Engraved 2' x 2' Tiles:",
      collapse: true,
      items: [
        { sectionSubtitle: "Zen Garden Family:" },
        ...SHAPES.filter((s: Shape) =>
          ["EngravedTangent", "EngravedLargeParallel", "EngravedZen"].includes(
            s.id,
          ),
        ),
        { sectionSubtitle: "Other Tiles:" },
        ...SHAPES.filter((s: Shape) =>
          [
            "EngravedKnot",
            "EngravedConverge",
            "EngravedSubway",
            "EngravedOffset",
            "EngravedTopo",
            "EngravedRiver",
          ].includes(s.id),
        ),
      ],
    },
    {
      title: "Printed 2' x 2' Tiles:",
      collapse: true,
      items: [
        ...SHAPES.filter((s: Shape) =>
          ["Aspen", "Bamboo", "Geo", "Pinstripe", "Blossom", "Strand"].includes(s.id),
        ),
      ],
    },
    {
      title: "Dimensional Tiles:",
      collapse: true,
      items: [
        ...SHAPES.filter((s: Shape) =>
          ["LargeUpperPeak", "LargeCenterPeak", "SmallPeak"].includes(s.id),
        ),
      ],
    },
  ],
  default: SHAPES.find((s: Shape) => "Square" === s.id),
};

export const findShapeById = (id: string): Shape | undefined => {
  const searchInShapes = (shapes: Shape[]): Shape | undefined => {
    for (const shape of shapes) {
      if (shape.id === id) return shape;
      if (shape.children?.length) {
        const foundInChildren = searchInShapes(shape.children);
        if (foundInChildren) return foundInChildren;
      }
    }
    return undefined;
  };
  return searchInShapes(SHAPES);
};
